/**
 * PebblesHeroCard — the student's "pebbles wallet" card in the dashboard hero row.
 *
 * Mirrors the parent dashboard's wallet card. Shows the lifetime pebble total prominently
 * (large icon + big Fredoka numeral so it fills the hero card) plus a real "+N this week"
 * figure derived client-side by summing the last 7 days of dailyPebbles (no backend change).
 * Renders the total as a raw integer (e.g. 1250) — matching how it appears elsewhere.
 */

import pebblesIcon from '@/assets/pebbles-icon.png';
import type { DailyPebbles } from '@/types/dashboard';

export interface PebblesHeroCardProps {
  totalPebbles: number;
  dailyPebbles: DailyPebbles[];
}

/** Sum pebbles earned across the most recent 7 daily entries. */
function pebblesThisWeek(dailyPebbles: DailyPebbles[]): number {
  return dailyPebbles.slice(0, 7).reduce((sum, d) => sum + (d.pebbles ?? 0), 0);
}

export function PebblesHeroCard({ totalPebbles, dailyPebbles }: PebblesHeroCardProps) {
  const thisWeek = pebblesThisWeek(dailyPebbles);
  const count = totalPebbles ?? 0;

  return (
    <div
      className="flex h-full flex-col items-center justify-center rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-100 p-6 text-center shadow-md"
      role="status"
      aria-label={`${count} total pebbles earned`}
    >
      <img
        src={pebblesIcon}
        alt=""
        aria-hidden="true"
        className="mb-2 h-16 w-16 drop-shadow-sm"
      />
      <span className="font-display text-5xl font-bold leading-none text-pebbles-600">{count}</span>
      <p className="mt-3 text-base font-semibold text-otter-earth-700">total pebbles earned</p>
      {thisWeek > 0 && (
        <p className="mt-2 text-sm font-bold text-amber-600">+{thisWeek} this week 🎉</p>
      )}
    </div>
  );
}
